import React, { useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Form,
  Input,
  Select,
  Button,
  Modal,
  message,
} from "antd";
import axios from "axios";
import Constants from "../Utils/Constants";
import useToken from "../Authentication/useToken";

function AddCaregiver({ patient, refetchPatients, onClose }) {
  const { t } = useTranslation("Patients");
  const { token } = useToken();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);

    const headers = { headers: { Authorization: "Bearer " + token } };

    const caregiverPayload = {
      firstname: data.caregiverFirstName?.trim(),
      lastname: data.caregiverLastName?.trim(),
      phoneNumber: data.caregiverPhone?.trim(),
      email: data.caregiverEmail?.trim()?.toLowerCase(),
      relationship: data.relationship,
    };

    try {
      // 1) Create caregiver (or reuse existing caregiver if email already exists)
      let caregiverId = null;

      try {
        const created = await axios.post(
          `${Constants.SERVER_URL}/create-caregiver`,
          caregiverPayload,
          headers
        );
        caregiverId = created.data?.id;
      } catch (err) {
        const status = err?.response?.status;

        // If caregiver already exists -> fetch caregivers list and reuse the existing id
        if (status === 409) {
          const list = await axios.get(
            `${Constants.SERVER_URL}/caregivers`,
            headers
          );
          const normalizedEmail = caregiverPayload.email;
          const existing = Array.isArray(list.data)
            ? list.data.find(
                (c) =>
                  String(c.email || "").trim().toLowerCase() === normalizedEmail
              )
            : null;

          caregiverId = existing?.id ?? null;
        } else {
          throw err;
        }
      }

      if (!caregiverId) {
        message.error(t("error_caregiver_add"));
        setLoading(false);
        return;
      }

      // 2) Create the Patient_Caregiver association (aligned with Thunder Client request)
      const associationPayload = {
        date: new Date().toISOString(),
        ProgramEnrollementId: null, // keep null unless you already have a ProgramEnrollement id
        CaregiverId: caregiverId,
        PatientId: patient.id,
      };

      await axios.post(
        `${Constants.SERVER_URL}/create-patient-caregiver`,
        associationPayload,
        headers
      );

      message.success(t("success_caregiver_add"));
      refetchPatients();
      onClose(true);
    } catch (error) {
      console.error("Error adding caregiver:", error);
      message.error(
        t(`Backend:${error.response?.data?.message}`) || t("error_caregiver_add")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("title_caregiver_add")}
      open={true}
      onCancel={() => onClose(false)}
      footer={[
        <Button key="cancel" onClick={() => onClose(false)}>
          {t("button_cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit(onSubmit)}
        >
          {t("button_submit")}
        </Button>,
      ]}
      width={800}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t("label_first_name")}
              validateStatus={errors.caregiverFirstName ? "error" : ""}
              help={errors.caregiverFirstName?.message}
            >
              <Controller
                name="caregiverFirstName"
                control={control}
                rules={{
                  required: t("error_first_name_required"),
                  minLength: {
                    value: 2,
                    message: t("error_first_name_min_length"),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t("placeholder_caregiver_first_name")}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("label_last_name")}
              validateStatus={errors.caregiverLastName ? "error" : ""}
              help={errors.caregiverLastName?.message}
            >
              <Controller
                name="caregiverLastName"
                control={control}
                rules={{
                  required: t("error_last_name_required"),
                  minLength: {
                    value: 2,
                    message: t("error_last_name_min_length"),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t("placeholder_caregiver_last_name")}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t("label_email")}
              validateStatus={errors.caregiverEmail ? "error" : ""}
              help={errors.caregiverEmail?.message}
            >
              <Controller
                name="caregiverEmail"
                control={control}
                rules={{
                  required: t("error_email_required"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("error_email_invalid"),
                  },
                }}
                render={({ field }) => (
                  <Input
                    type="email"
                    {...field}
                    placeholder={t("placeholder_caregiver_email")}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("label_phone_number")}
              validateStatus={errors.caregiverPhone ? "error" : ""}
              help={errors.caregiverPhone?.message}
            >
              <Controller
                name="caregiverPhone"
                control={control}
                rules={{
                  required: t("error_phone_number_required"),
                  pattern: {
                    value: /^[0-9+\s-]{8,}$/,
                    message: t("error_phone_number_invalid"),
                  },
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t("placeholder_caregiver_phone")}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t("label_relationship")}
              validateStatus={errors.relationship ? "error" : ""}
              help={errors.relationship?.message}
            >
              <Controller
                name="relationship"
                control={control}
                rules={{
                  required: t("error_relashionship_required"),
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder_relationship")}
                  >
                    <Select.Option value="spouse">{t("option_spouse") || "spouse"}</Select.Option>
                    <Select.Option value="child">{t("option_child") || "child"}</Select.Option>
                    <Select.Option value="parent">
                      {t("option_parentParent")}
                    </Select.Option>
                    <Select.Option value="sibling">
                      {t("option_sibling")}
                    </Select.Option>
                    <Select.Option value="friend">
                      {t("option_friend")}
                    </Select.Option>
                    <Select.Option value="other">
                      {t("option_other")}
                    </Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

AddCaregiver.propTypes = {
  patient: PropTypes.object.isRequired,
  refetchPatients: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired, // onClose should accept a success parameter
};

export default AddCaregiver;
